<template>
  <!--laptop(>960) and larger-->
  <v-card
    v-if="!isMobile"
    flat
    class="
      rounded-lg
      px-6 px-md-3
      subtitle-1
      font-weight-regular
      textLight--text
    "
  >
    <v-row align="center">
      <v-col cols="12" md>
        <span class="text-ellipse-1">
          {{ getLocalisedValue(data.trainingTitle) }}
        </span>
      </v-col>

      <v-col cols="12" sm="6" md>
        <span class="text-ellipse-1">
          {{ trainerName }}
        </span>
      </v-col>

      <v-col cols="12" sm="6" md>
        <span>
          {{ "-" }}
        </span>
      </v-col>

      <v-col cols="auto" md="2" xl="1" :class="expiryInfo.warningClass">
        <span> {{ expiryInfo.expiryDate }} </span>

        <v-icon
          v-if="expiryInfo.warningIcon"
          :class="expiryInfo.warningClass"
          small
          class="px-2"
        >
          {{ expiryInfo.warningIcon }}
        </v-icon>
      </v-col>

      <v-col v-if="$vuetify.breakpoint.mdAndUp" cols="5" md="2" xl="1">
        <span>
          {{ certificateDate }}
        </span>
      </v-col>

      <v-spacer v-else></v-spacer>

      <v-col cols="5" md="1" xl="1" class="d-flex justify-center">
        <v-icon
          @click="navigateToCourseCertificatePage(data._id)"
          color="textLight"
          >mdi-open-in-new
        </v-icon>
      </v-col>

      <v-col cols="auto" md="auto" class="text-end">
        <v-icon v-if="expiryInfo.infoIcon" :class="expiryInfo.infoClass">
          {{ expiryInfo.infoIcon }}
        </v-icon>

        <v-icon v-else style="visibility: hidden">
          mdi-check-circle-outline
        </v-icon>
      </v-col>
    </v-row>
  </v-card>

  <!--tablet(<960) and smaller-->
  <v-card
    v-else
    flat
    class="rounded-lg px-4 py-2 subtitle-2 font-weight-regular textLight--text"
  >
    <v-row no-gutters align="center">
      <v-col cols>
        <span style="font-size: 10px; color: #2a2e31; opacity: 0.5">
          {{ providerName || "-" }}
        </span>
      </v-col>

      <v-col cols="auto">
        <v-icon
          size="20"
          @click="navigateToCourseCertificatePage(data._id)"
          color="textLight"
          style="margin-inline-end: 3px"
        >
          mdi-open-in-new
        </v-icon>
      </v-col>

      <v-col cols="12" md>
        <span class="font-weight-bold text-ellipse-1">
          {{ getLocalisedValue(data.trainingTitle) }}
        </span>
      </v-col>

      <v-col cols="12" md>
        <span class="primary--text text-ellipse-1" style="font-size: 10px">
          {{ trainerName }}
        </span>
      </v-col>

      <v-col
        cols="auto"
        md="2"
        xl="1"
        :class="expiryInfo.warningClass"
        style="font-size: 10px"
      >
        <span> {{ expiryInfo.expiryDate }} </span>

        <v-icon
          v-if="expiryInfo.warningIcon"
          :class="expiryInfo.warningClass"
          size="10"
          class="px-1"
        >
          {{ expiryInfo.warningIcon }}
        </v-icon>
      </v-col>

      <v-spacer></v-spacer>

      <v-col cols="auto" md="auto" class="text-end">
        <v-icon v-if="expiryInfo.infoIcon" :class="expiryInfo.infoClass">
          {{ expiryInfo.infoIcon }}
        </v-icon>

        <v-icon v-else style="visibility: hidden">
          mdi-check-circle-outline
        </v-icon>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import moment from "moment";

export default {
  name: "TrainingCertificatesTableRow",
  props: {
    data: {
      type: Object,
      required: true,
    },
    providerName: String,
  },
  computed: {
    isMobile() {
      return !this.$vuetify.breakpoint.mdAndUp;
    },
    trainerName() {
      return this.data.trainerName?.trim()?.length
        ? this.data.trainerName
        : "-";
    },
    expiryInfo() {
      if (this.data.expiresAt) {
        const expiryDate = this.$moment(parseInt(this.data.expiresAt));
        let formattedExpiryDate = expiryDate.format("DD MMM YYYY");
        const daysDifference = this.$moment
          .duration(expiryDate.diff(this.$moment()))
          .asDays();

        const isExpired = daysDifference < 0;
        const isAboutToExpire = !isExpired && daysDifference <= 5;
        let warningClass, warningIcon, infoClass, infoIcon;

        if (isExpired) {
          if (this.isMobile) {
            formattedExpiryDate = "Expired on " + formattedExpiryDate;
          }

          warningClass = "error--text";
          warningIcon = "mdi-alert-outline";
          infoClass = "";
          infoIcon = "mdi-minus-circle-outline";
        } else if (isAboutToExpire) {
          if (this.isMobile) {
            formattedExpiryDate = "Valid up to " + formattedExpiryDate;
          }

          warningClass = "warning--text";
          warningIcon = "mdi-alert-outline";
          infoClass = "warning--text";
          infoIcon = "mdi-alert-circle-outline";
        } else {
          if (this.isMobile) {
            formattedExpiryDate = "Valid up to " + formattedExpiryDate;
          }

          warningClass = "";
          warningIcon = null;
          infoClass = "success--text";
          infoIcon = "mdi-check-circle-outline";
        }

        return {
          expiryDate: formattedExpiryDate,
          warningClass,
          warningIcon,
          infoClass,
          infoIcon,
        };
      } else {
        return {
          expiryDate: "-",
          warningClass: "",
          warningIcon: null,
          infoClass: "",
          infoIcon: null,
        };
      }
    },
    certificateDate() {
      return this.data.completedAt
        ? moment(parseInt(this.data.completedAt)).format("DD MMM YYYY")
        : "-";
    },
  },
  methods: {
    navigateToCourseCertificatePage(id) {
      const routeData = this.$router.resolve({
        name: "PublicCourseCertificate",
        params: { id },
      });

      window.open(routeData.href, "_blank");
    },
  },
};
</script>

<style scoped></style>
